import { MARKETABLE_CONTACTS_ACCESS, MARKETABLE_CONTACTS_WRITE } from 'pricing-pages-lib/constants/Scopes';
import { createSelector } from 'reselect';
import { MARKETING_PLUS_SUITE } from 'self-service-api/constants/BundleTypes';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import { FetchMarketingContactsRequestTypes } from '../actions/MarketingContactsActions';
import * as RequestStatus from '../actions/constants/RequestStatus';
import { hasProductLineSelectorInProductInventorySelector, hasProductVersionOrNewerInProductInventorySelector, highestProductLevelFromProductInventorySelector } from './ProductInventorySelectors';
import { crmSuiteProductLevelPreSeatsAndSeatsOnlySelector, getBundleTypeSelector, productForSkuIdSelector } from './ProductsSelectors';
import { makeRequestStatusSelector } from './RequestStatusSelectors';
import { hasScopeSelector } from './ScopeSelectors';
export const fetchMarketingContactsStatus = makeRequestStatusSelector(FetchMarketingContactsRequestTypes.REQUEST);
export const hasMarketingContactsWriteScopeSelector = state => {
  return hasScopeSelector(state, MARKETABLE_CONTACTS_WRITE);
};
export const isOnMCPlanSelector = state => {
  return hasScopeSelector(state, MARKETABLE_CONTACTS_ACCESS);
};
const ownsPaidMarketingHubSelector = state => hasProductLineSelectorInProductInventorySelector(state, {
  productLine: ProductLines.MARKETING
});
const portalHasAccessToEligibilityFlowSelector = createSelector([hasMarketingContactsWriteScopeSelector, ownsPaidMarketingHubSelector], (hasMarketingContactsWriteScope, ownsPaidMarketingHub) => hasMarketingContactsWriteScope || ownsPaidMarketingHub);
export const marketingContactsOnRenewalSelector = state => {
  const {
    marketingContacts
  } = state;
  const marketingContactsStatus = fetchMarketingContactsStatus(state);

  // Anyone without the write scope defaults to all contacts non-MC on purchase
  if (!portalHasAccessToEligibilityFlowSelector(state)) return 0;
  if (!marketingContacts || marketingContactsStatus === RequestStatus.FAILED) {
    return null;
  }

  // We need to use different CRM search calls depending on if the portal has MC or not
  if (isOnMCPlanSelector(state)) {
    // The user is on MC, so the api call uses both the marketing status
    // and the marketing until next update status
    return marketingContacts.onMCRenewal;
  }

  // The user isn't on MC, so the api call doesn't pay attention to the
  // marketing status field, just marketing until next update
  return marketingContacts.onSwitchToMC;
};
export const isMarketingContactsSidegradeSelector = (state, productConfigurations) => {
  var _VersionNumbers$check;
  if (!(productConfigurations !== null && productConfigurations !== void 0 && productConfigurations.length)) {
    return false;
  }
  const suiteProductLevel = crmSuiteProductLevelPreSeatsAndSeatsOnlySelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  });

  // we only care about marketing contacts sidegrades when marketing hub is being purchased by itself
  if (suiteProductLevel) {
    return false;
  }
  const checkoutMarketingProduct = productConfigurations.map(({
    skuId
  }) => productForSkuIdSelector(state, {
    skuId
  })).find(product => {
    const bundleType = getBundleTypeSelector(state, {
      apiName: product.apiName
    });
    return (product.productLine === ProductLines.MARKETING || bundleType === MARKETING_PLUS_SUITE) && product.productLevel;
  });
  const isTryingToBuySameMarketingProductLevelAlreadyOwned = highestProductLevelFromProductInventorySelector(state, {
    productLine: ProductLines.MARKETING
  }) === (checkoutMarketingProduct === null || checkoutMarketingProduct === void 0 ? void 0 : checkoutMarketingProduct.productLevel);
  const alreadyOwnsMarketingContacts = checkoutMarketingProduct !== null && checkoutMarketingProduct !== void 0 && checkoutMarketingProduct.apiName ? hasProductVersionOrNewerInProductInventorySelector(state, {
    apiName: checkoutMarketingProduct.apiName,
    versionNumber: VersionNumbers[checkoutMarketingProduct.apiName].MARKETING_CONTACTS
  }) : false;
  if (!(checkoutMarketingProduct !== null && checkoutMarketingProduct !== void 0 && checkoutMarketingProduct.apiName) || !(checkoutMarketingProduct !== null && checkoutMarketingProduct !== void 0 && checkoutMarketingProduct.currentVersion.versionNumber)) {
    return false;
  }
  const isTryingToBuyMarketingHubVersionThatHasMarketingContacts = checkoutMarketingProduct.currentVersion.versionNumber >= ((_VersionNumbers$check = VersionNumbers[checkoutMarketingProduct.apiName]) === null || _VersionNumbers$check === void 0 ? void 0 : _VersionNumbers$check.MARKETING_CONTACTS);
  return isTryingToBuyMarketingHubVersionThatHasMarketingContacts && isTryingToBuySameMarketingProductLevelAlreadyOwned && !alreadyOwnsMarketingContacts;
};